/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
// Adapted from variant of JSONView Chrome extension
// https://github.com/jamiew/jsonview-chrome
import Component from '@glimmer/component';

import escape from 'cio/utils/escape';
import formatError from 'cio/utils/errors/format-error';
import { htmlSafe } from '@ember/template';
import EnvironmentService from 'cio/services/environment';
import { isAttributeEnabledIdentifier } from 'cio/utils/identifiers';
import { isValidTimestamp } from '@customerio/fly-components/utils/validators/timestamp';
import MomentService from 'cio/services/moment';

interface JsonFormatterArgs {
  data: any;
  containsIdentifiers?: boolean;
  showNestedIdentifiers?: boolean;
  reservedAttributes?: string[];
  formatErrorKey?: string;
  formatErrorType?: string;
  hideDate?: boolean;
  renderHTML?: boolean;
}

export default class JsonFormatter extends Component<JsonFormatterArgs> {
  @service moment!: MomentService;
  @service environment!: EnvironmentService;

  get containsIdentifiers() {
    return this.args.containsIdentifiers ?? false;
  }
  get showNestedIdentifiers() {
    return this.args.showNestedIdentifiers ?? false;
  }
  get reservedAttributes() {
    return this.args.reservedAttributes ?? [];
  }
  get formatErrorKey() {
    return this.args.formatErrorKey ?? null;
  }
  get formatErrorType() {
    return this.args.formatErrorType ?? null;
  }
  get hideDate() {
    return this.args.hideDate ?? false;
  }

  @computed('args.data')
  get pretty() {
    return this.valueToHTML(this.args.data);
  }

  // Convert a basic JSON datatype (number, string, boolean, null, object, array) into an HTML fragment
  valueToHTML(value: any, parentKey: string | undefined = undefined, key: string | undefined = undefined) {
    let valueType = typeof value;
    let result;

    if (value === null) {
      result = this.decorateWithSpan('null', 'null');
    } else if (value && value.constructor === Array) {
      result = this.arrayToHTML(value, parentKey);
    } else if (valueType === 'object') {
      result = this.objectToHTML(value, parentKey);
    } else if (valueType === 'number') {
      result = this.decorateWithSpan(value, 'num', true, key);
    } else if (valueType === 'string') {
      let escapeValue = true;

      // using separate "if" to improve readability without inversed ifs
      if (this.args.renderHTML === true) {
        escapeValue = false;
      }

      if (this.formatErrorKey && this.formatErrorKey === parentKey) {
        try {
          value = htmlSafe(formatError(value, this.formatErrorType));
          escapeValue = false;
        } catch (e) {
          // Formatter not found, ignore error and proceed
        }
      }

      let isLink = /^(http|https):\/\/[^\s]+$/.test(value);
      if (isLink) {
        result = `"<a href="${value}" target="_blank" rel="noopener noreferrer">${escape(value)}</a>"`;
      } else {
        result = this.decorateWithSpan(`"${value}"`, 'string', escapeValue, key);
      }
    } else if (valueType === 'boolean') {
      result = this.decorateWithSpan(value.toString(), 'bool');
    }

    return result;
  }

  decorateWithSpan(value: any, className: string, escapeValue = true, key: string | undefined = undefined) {
    let decoratedSpan = `<span class="${className} fs-mask">${escapeValue ? escape(value) : value}</span>`;
    // remove possible extra starting/ending quotation marks '"{time}"' from value string, before checking for valid timestamp
    let unwrapped = value?.replace?.(/^"|"$/g, '') ?? value;
    let possibleDate =
      !this.hideDate &&
      (key == null || !isAttributeEnabledIdentifier(key, this.environment.current?.identifiers)) &&
      isValidTimestamp(unwrapped);
    if (possibleDate) {
      let formattedTimestamp = this.moment.formatTimestamp([unwrapped], {
        capitalize: true,
        finalFormatter: (timestampAsMoment) => timestampAsMoment.calendar(),
      });
      decoratedSpan += `<span class="value-timestamp ml-2">${formattedTimestamp}</span>`;
    }
    return decoratedSpan;
  }

  // Convert an array into an HTML fragment
  arrayToHTML(array: any[], parentKey: string | undefined = undefined) {
    let output = '[<ul class="array" data-collapsible>';
    let hasContents = false;
    let element;
    let _len = array.length;

    for (let _i = 0; _i < _len; _i++) {
      element = array[_i];
      hasContents = true;
      output += '<li>';

      if ((element && element.constructor === Array) || (typeof element === 'object' && element !== null)) {
        output += '<div class="collapser" data-collapser>-</div>';
      }

      output += `${this.valueToHTML(element, parentKey)}</li>`;
    }

    output += '</ul>]';

    if (!hasContents) {
      output = '[ ]';
    }

    return output;
  }

  // Convert a JSON object to an HTML fragment
  objectToHTML(object: Record<string, any>, parentKey: string | undefined = undefined) {
    let output = '{<ul class="obj" data-collapsible>';
    let hasContents = false;
    let key;
    let value;

    for (key in object) {
      value = object[key];
      hasContents = true;
      output += '<li>';

      if ((value && value.constructor === Array) || (typeof value === 'object' && value !== null)) {
        output += '<div class="collapser" data-collapser>-</div>';
      }

      let filterKey = escape(key);

      if (parentKey) {
        filterKey = `${parentKey}.${filterKey}`;
      }

      output += `<span class="prop">${escape(key)}</span>${this.attributeTypeLabel(
        key,
        !parentKey,
      )}: ${this.valueToHTML(value, filterKey, key)}</li>`;
    }

    output += '</ul>}';

    if (!hasContents) {
      output = '{ }';
    }

    return output;
  }

  attributeTypeLabel(attribute: string, isRootKey: boolean) {
    if (!isRootKey && !this.showNestedIdentifiers) {
      return '';
    }

    let isIdentifier =
      this.containsIdentifiers && isAttributeEnabledIdentifier(attribute, this.environment.current?.identifiers);
    let isReserved = this.reservedAttributes.includes(attribute);

    if (!isIdentifier && !isReserved) {
      return '';
    }

    return `<span class="fly-label fly-label--sm is-pending ml-1 align-baseline" data-test-attribute-type-label="">${
      isIdentifier ? 'Identifier' : 'Reserved'
    }</span>`;
  }

  // Click handler for collapsing and expanding objects and arrays
  collapse(event: Event) {
    let collapser = event.target as HTMLElement | undefined;
    if (collapser == null || !('collapser' in collapser.dataset)) {
      return;
    }

    let target = collapser.parentNode?.querySelector('[data-collapsible]') as HTMLDivElement | undefined;
    if (target == null) {
      return;
    }

    if (target.style.display === 'none') {
      let ellipsisEle = target.parentNode?.querySelector('[data-ellipsis]');
      if (ellipsisEle != null) {
        target.parentNode?.removeChild<Element>(ellipsisEle);
      }
      target.style.display = '';
    } else {
      target.style.display = 'none';
      let ellipsis = document.createElement('span');
      ellipsis.dataset.ellipsis = '';
      ellipsis.innerHTML = ' &hellip; ';
      target.parentNode?.insertBefore(ellipsis, target);
    }

    if (collapser.innerHTML === '-') {
      collapser.innerHTML = '+';
    } else {
      collapser.innerHTML = '-';
    }
  }
}
